'use client';

import { useMemo, type FC } from 'react';
import { Alert } from 'react-bootstrap';
import { TriangleAlert } from 'lucide-react';
import { useAuiState } from '@assistant-ui/react';

export type McpUserApiKeyWarning = {
  code: string;
  message: string;
};

const EMPTY_WARNINGS: McpUserApiKeyWarning[] = [];

function readWarnings(value: unknown): McpUserApiKeyWarning[] {
  if (!Array.isArray(value)) return EMPTY_WARNINGS;
  const out: McpUserApiKeyWarning[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    const code = typeof row.code === 'string' ? row.code.trim() : '';
    const message = typeof row.message === 'string' ? row.message.trim() : '';
    if (!code || !message || seen.has(code)) continue;
    seen.add(code);
    out.push({ code, message });
  }
  return out.length > 0 ? out : EMPTY_WARNINGS;
}

/** Warning alerts under assistant reply when user API key failed and Omni was used. */
export const McpUserApiKeyWarningAlert: FC = () => {
  // useAuiState uses Object.is — never return a fresh array/object from the selector.
  const warningsKey = useAuiState((s) => {
    if (s.message.role !== 'assistant') return '';
    const raw = s.message.metadata?.custom?.userApiKeyWarnings;
    if (!Array.isArray(raw) || raw.length === 0) return '';
    try {
      return JSON.stringify(raw);
    } catch {
      return '';
    }
  });

  const warnings = useMemo(
    () => (warningsKey ? readWarnings(JSON.parse(warningsKey)) : EMPTY_WARNINGS),
    [warningsKey]
  );

  if (warnings.length === 0) return null;

  return (
    <div className="mt-3 d-flex flex-column gap-2">
      {warnings.map((warning) => (
        <Alert
          key={warning.code}
          variant="warning"
          className="mb-0 py-2 px-3 fs--1 d-flex align-items-start gap-2"
        >
          <TriangleAlert
            className="flex-shrink-0 mt-1"
            size={16}
            strokeWidth={2}
            aria-hidden
          />
          <span className="flex-grow-1">{warning.message}</span>
        </Alert>
      ))}
    </div>
  );
};
